import { useMemo } from 'react';
import { useAssistantTool } from '@assistant-ui/react';
import { queryPayouts } from '../../../utils/RocksetQueries/PayoutsQueries';
import { P } from '../../../utils/permissions';
import { DASHBOARD_ASSISTANT_RUNTIME_MODE } from '../constants';
import { EMPTY_TOOL_PARAMETERS } from '../lib/assistantToolSchemas';
import { getMerchantNames } from '../lib/helpers';
import { hasPermission, restrictedResult } from '../lib/permissions';
import {
  createFrontendToolActionMessage,
  createFrontendToolErrorMessage,
} from '../lib/toolActionMessages';
import { serializePayout } from '../lib/payoutSerialization';

const DATE_OPTIONS = { year: 'numeric', month: 'short', day: 'numeric' };

function getDefaultPayoutDateRange() {
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 6000);

  return `${startDate.toLocaleDateString('en-US', DATE_OPTIONS)} - ${endDate.toLocaleDateString(
    'en-US',
    DATE_OPTIONS,
  )}`;
}

export function useGetLatestPayoutAction({
  activeSessionId,
  dispatchActionMessage,
  organizationName,
}) {
  const tool = useMemo(
    () => ({
      toolName: 'get_latest_payout',
      type: 'frontend',
      description:
        'Fetch the most recent payout for the active organization. Use this when the user asks for the latest, most recent, current, or last payout amount, status, arrival date, or payout details. Prefer this over open_payouts when the user asks for payout data.',
      parameters: EMPTY_TOOL_PARAMETERS,
      execute: async () => {
        if (!hasPermission(P.VIEW_PAYOUTS)) {
          const result = restrictedResult('payouts');
          dispatchActionMessage(
            createFrontendToolErrorMessage({
              message: result.message,
              status: result.status,
              toolName: 'get_latest_payout',
              toolResult: result,
            }),
          );
          return result;
        }

        const merchants = await getMerchantNames(organizationName);
        if (merchants.length === 0) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'No active organization name is available for payout lookup.',
          };
          dispatchActionMessage(
            createFrontendToolErrorMessage({
              message: result.message,
              status: result.status,
              toolName: 'get_latest_payout',
              toolResult: result,
            }),
          );
          return result;
        }

        const payouts = await queryPayouts(
          null,
          merchants,
          '',
          '',
          '',
          [],
          [],
          [],
          getDefaultPayoutDateRange(),
          'CREATED_DATE',
          1,
        );
        const latestPayout = Array.isArray(payouts) ? payouts.find(Boolean) : null;

        const result = latestPayout
          ? {
              runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
              status: 'ok',
              organizationName,
              latestPayout: serializePayout(latestPayout),
            }
          : {
              runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
              status: 'empty',
              organizationName,
              latestPayout: null,
              message: 'No payouts were found for the active organization.',
            };
        const displayPayload = {
          renderer: 'dashboard_payout_lookup',
          summary:
            result.status === 'ok'
              ? `Checked latest payout${
                  result.latestPayout?.netAmountFormatted
                    ? `: ${result.latestPayout.netAmountFormatted}`
                    : ''
                }`
              : 'Checked latest payout: no payouts found',
          data: result,
        };

        dispatchActionMessage(
          createFrontendToolActionMessage({
            actionType: 'search',
            content: displayPayload.summary,
            displayPayload,
            status: result.status,
            toolArgs: { activeSessionId },
            toolName: 'get_latest_payout',
            toolResult: result,
          }),
        );

        return {
          ...result,
          guidance:
            result.status === 'ok'
              ? 'Answer the user from latestPayout. Use latestPayout.netAmountFormatted and latestPayout.costFormatted for user-facing money amounts. latestPayout.netAmountCents and latestPayout.costCents are raw cents; never display them as dollars. Do not call open_payouts unless they ask to open the payouts page.'
              : 'Tell the user that no payouts were found. Do not claim there is a latest payout.',
        };
      },
    }),
    [activeSessionId, dispatchActionMessage, organizationName],
  );

  useAssistantTool(tool);
}
